import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Grid2 as Grid,
	Typography,
	useMediaQuery,
	useTheme,
	InputAdornment,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import StatusLabel from "../../StatusLabel/StatusLabel";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { CalendarIcon } from "@mui/x-date-pickers/icons";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { enAU } from "date-fns/locale";

import ReceiveFailureAlert from "../../ReceiveFailureAlert/ReceiveFailureAlert";
import { Transfer } from "../../../common/Transfer";
import { useCallback, useState } from "react";
import { useStyles } from "../Transfer.styles";
import CopyToClipboard from "../../CopyToClipboard/CopyToClipboard";
import { transferSourceSystem } from "../../../util/transferSourceSystem";
import ReceiveComments from "../../Comment/ReceiveComments";
import { Receive, ReceiveComment } from "../../../common/Receive";
import UnverifiedTransferWarning from "./UnverifiedTransferWarning";
import { AdvancedShippingNotice } from "@/common/AdvancedShippingNotice";

import AsnButton from "@/components/Asn/AsnButton";
import { DatePickerMobileActionBar } from "@/components/Common/DatePickerMobileActionBar";
import { getCustomerOrderNumber } from "@/util/transferUtil";

export type ReceiveTransferHeaderDetailsProps = {
	transfer: Transfer;
	receiveStatus: string;
	receiveFailureReason?: string | null;
	receivedDate: Date | null;
	onReceiveDateChanged: (date: Date | null) => void;
	headerIsExpanded: boolean;
	onToggleHeader: (isExpanded: boolean) => void;
	comments: ReceiveComment[];
	receive?: Receive | null;
	transferId?: string | null;
	isUnverified?: boolean;
	isDeliveredDateDisabled?: boolean;
	asns: AdvancedShippingNotice[];
};

const ReceiveTransferHeaderDetails = ({
	transfer,
	receiveStatus,
	receiveFailureReason,
	receivedDate,
	onReceiveDateChanged,
	headerIsExpanded,
	onToggleHeader,
	comments,
	receive,
	transferId,
	isUnverified,
	isDeliveredDateDisabled,
	asns,
}: ReceiveTransferHeaderDetailsProps) => {
	const classes = useStyles();

	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const transferNumber =
		transfer.sourceSystem.toLowerCase() === transferSourceSystem.Algo
			? transfer.sourceId
			: transfer.transferRequestId
				? transfer.transferRequestId
				: transfer.sourceId;
	const customerOrderNumber = getCustomerOrderNumber(transfer.references);
	const totalQuantity =
		transfer.sourceSystem.toLowerCase() === transferSourceSystem.Intranet ||
		transfer.sourceSystem.toLowerCase() === transferSourceSystem.ReceivingApp
			? transfer.totalApprovedQuantity
			: transfer.totalInTransitQuantity;

	const [asnDetailsModalOpen, setAsnDetailsModalOpen] = useState(false);

	const onAsnDetailsModalOpen = useCallback(() => {
		setAsnDetailsModalOpen(true);
	}, []);

	const onAsnDetailsModalClose = useCallback(() => {
		setAsnDetailsModalOpen(false);
	}, []);

	return (
		<>
			<Accordion
				expanded={headerIsExpanded}
				className={classes.transferReceiveHeaderRoot}
				onChange={(_, expanded) => onToggleHeader(expanded)}
			>
				<AccordionSummary
					expandIcon={<ExpandMoreIcon />}
					aria-controls="invoiceheaderdetails-content"
					id="invoicerheaderdetails"
					className={classes.transferReceiveHeader}
				>
					<Grid container alignItems="center" sx={{ width: "100%" }}>
						<Grid
							container
							direction={isMobile ? "column" : "row"}
							alignItems={isMobile ? "flex-start" : "center"}
							size={{ xs: 7, sm: 6 }}
							className={classes.transferNumberContainer}
						>
							<Typography variant="caption" color="textSecondary" component="div">
								Transfer Number
							</Typography>
							{transferNumber && (
								<Box display="flex" alignItems="center">
									<Typography variant="body2" component="div">
										{" " + transferNumber}
									</Typography>
									<CopyToClipboard text={transferNumber} />
								</Box>
							)}
						</Grid>
						<Grid container justifyContent="flex-end" size="grow">
							<Grid>
								<StatusLabel status={receiveStatus} size={isMobile ? "extraNarrow" : "medium"} />
							</Grid>
						</Grid>
					</Grid>
				</AccordionSummary>
				<AccordionDetails>
					<Grid container spacing={1}>
						{customerOrderNumber && (
							<Grid container size={12}>
								<Grid size={6} className={classes.transferItemCol1}>
									<Typography variant="caption" color="textSecondary" component="div">
										Customer Order Number
									</Typography>
									<Typography variant="body2" component="div">
										{customerOrderNumber}
									</Typography>
								</Grid>
								<Grid size={6} className={classes.transferItemCol2}>
									<Typography variant="caption" color="textSecondary" component="div">
										Total Qty
									</Typography>
									<Typography variant="body2" component="div">
										{totalQuantity}
									</Typography>
								</Grid>
							</Grid>
						)}
						<Grid container size={12}>
							<Grid size={6} className={classes.transferItemCol1}>
								<Typography variant="caption" color="textSecondary" component="div">
									Transfer Status
								</Typography>
								<Typography variant="body2" component="div">
									{transfer.status}
								</Typography>
							</Grid>
							<Grid size={6} className={classes.transferItemCol2}>
								<Typography variant="caption" color="textSecondary" component="div">
									Source System
								</Typography>
								<Typography variant="body2" component="div">
									{transfer.sourceSystem}
								</Typography>
							</Grid>
						</Grid>

						<Grid container size={12}>
							<Grid size={6} className={classes.transferItemCol1}>
								<Typography variant="caption" color="textSecondary" component="div">
									Label ID
								</Typography>
								{transfer.labelId && (
									<Box display="flex" alignItems="center">
										<Typography component="div" variant="body2">
											{transfer.labelId}
										</Typography>
										<CopyToClipboard text={transfer.labelId} />
									</Box>
								)}
							</Grid>
							<Grid size={6} className={classes.transferItemCol2}>
								<Typography variant="caption" color="textSecondary" component="div">
									Transfer Type
								</Typography>
								<Typography variant="body2" component="div">
									{transfer.type}
								</Typography>
							</Grid>
						</Grid>

						<Grid container size={12}>
							<Grid size={6} className={classes.transferItemCol1}>
								<Typography variant="caption" color="textSecondary" component="div">
									To
								</Typography>
								<Typography variant="body2" component="div">
									{transfer.toLocation.locationId} - {transfer.toLocation.locationName}
								</Typography>
							</Grid>
							<Grid size={6} className={classes.transferItemCol2}>
								<Typography variant="caption" color="textSecondary" component="div">
									Transfer Reason
								</Typography>
								<Typography variant="body2" component="div">
									{transfer.reason}
								</Typography>
							</Grid>
						</Grid>

						<Grid container size={12}>
							<Grid size={6} className={classes.transferItemCol1}>
								<Typography variant="caption" color="textSecondary" component="div">
									From
								</Typography>
								<Typography variant="body2" component="div">
									{transfer.fromLocation.locationId} - {transfer.fromLocation.locationName}
								</Typography>
							</Grid>
							<Grid size={6} className={classes.transferItemCol2}>
								{customerOrderNumber ? (
									<>
										<Typography variant="caption" color="textSecondary" component="div">
											Created By
										</Typography>
										<Typography variant="body2" component="div">
											{transfer.requesterName} ({transfer.requesterId})
										</Typography>
									</>
								) : (
									<>
										<Typography variant="caption" color="textSecondary" component="div">
											Total Qty
										</Typography>
										<Typography variant="body2" component="div">
											{totalQuantity}
										</Typography>
									</>
								)}
							</Grid>
						</Grid>

						<Grid container size={12}>
							<Grid size={6} className={classes.transferItemCol1}>
								<Box display="flex" flexDirection="row">
									<Box>
										<LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enAU}>
											<DatePicker
												label="Delivered Date"
												value={receivedDate}
												onChange={onReceiveDateChanged}
												maxDate={new Date()}
												closeOnSelect
												disabled={isDeliveredDateDisabled}
												slots={{ actionBar: DatePickerMobileActionBar }}
												slotProps={{
													textField: {
														id: "received-date",
														size: "small",
														variant: "standard",
													},
													field: {
														InputProps: {
															endAdornment: (
																<InputAdornment position="end">
																	<CalendarIcon fontSize="small" />
																</InputAdornment>
															),
														},
													},
													actionBar: {
														actions: isMobile ? ["cancel", "accept"] : [],
														onSetDefaultDate: () => {
															if (!receivedDate) onReceiveDateChanged(new Date());
														},
													} as any,
												}}
											/>
										</LocalizationProvider>
									</Box>
								</Box>
							</Grid>
							{!customerOrderNumber && (
								<Grid size={6} className={classes.transferItemCol2}>
									<Typography variant="caption" color="textSecondary" component="div">
										Created By
									</Typography>
									<Typography variant="body2" component="div">
										{transfer.requesterName} ({transfer.requesterId})
									</Typography>
								</Grid>
							)}
						</Grid>
						<Grid container size={12} alignItems="center">
							<Grid size={6}>
								<ReceiveComments initialComments={comments} receiveId={receive?.id} transferId={transferId} compact />
							</Grid>
							{asns.length > 0 && (
								<Grid size={6}>
									<AsnButton
										isOpen={asnDetailsModalOpen}
										transfer={transfer}
										asns={asns}
										fromLocationId={String(transfer.fromLocation.locationId)}
										fromLocationName={transfer.fromLocation.locationName}
										toLocationId={String(transfer.toLocation.locationId)}
										toLocationName={transfer.toLocation.locationName}
										onAsnDetailsModalOpen={onAsnDetailsModalOpen}
										onAsnDetailsModalClose={onAsnDetailsModalClose}
										compact
									/>
								</Grid>
							)}
						</Grid>
						{receiveFailureReason != null && receiveFailureReason.length > 0 && (
							<Grid container size={12}>
								<Grid size={12} className={classes.receiveFailureReasonGrid}>
									<ReceiveFailureAlert status={receiveStatus} failureReason={receiveFailureReason} />
								</Grid>
							</Grid>
						)}
						{isUnverified && (
							<Grid container size={12}>
								<Grid size={12}>
									<UnverifiedTransferWarning isVisible={Boolean(isUnverified)} />
								</Grid>
							</Grid>
						)}
					</Grid>
				</AccordionDetails>
			</Accordion>
		</>
	);
};

export default ReceiveTransferHeaderDetails;
